import { DEFAULT_LOCALE, type Locale } from '../i18n/locale';

/**
 * Formatação e leitura de data digitada pelo usuário, num lugar só.
 *
 * O problema que isto resolve: o app lia e escrevia `dd/MM/yyyy` fixo. Um
 * usuário en-US digitando 3/4/2026 gravava 3 de abril sem erro nenhum, porque
 * nada nunca falhava, só interpretava diferente. Data errada no banco é pior do
 * que data recusada.
 *
 * Regras:
 *  - ISO (yyyy-mm-dd) é sempre aceito: não tem ambiguidade possível.
 *  - Em seguida vale a ORDEM DO IDIOMA do usuário. Quem digita no próprio
 *    formato tem que ser entendido sem atrito.
 *  - A outra ordem só é tentada quando a ordem do idioma é IMPOSSÍVEL
 *    (ex.: 13/25/2026 em en-US), porque aí só existe uma leitura válida.
 *  - Nada de "chutar" quando as duas leituras são válidas e diferentes: nesse
 *    caso a ordem do idioma vence, que é a única escolha defensável.
 *
 * O armazenamento continua sempre ISO. Isto aqui é só a fronteira de entrada e
 * saída da tela.
 */

export type DateOrder = 'dmy' | 'mdy';

/** Ordem de dia/mês por idioma. `es` aqui é LatAm, que usa dia/mês. */
export const DATE_ORDER: Record<Locale, DateOrder> = {
  'pt-BR': 'dmy',
  en: 'mdy',
  es: 'dmy',
};

/** Máscara exibida ao usuário (placeholder de input, dica de formato). */
export const DATE_MASK: Record<Locale, string> = {
  'pt-BR': 'dd/mm/aaaa',
  en: 'mm/dd/yyyy',
  es: 'dd/mm/aaaa',
};

const pad = (n: number): string => String(n).padStart(2, '0');

function toDate(value: Date | string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Data para exibição, na ordem do idioma. Devolve '' para entrada inválida. */
export function formatUserDate(
  value: Date | string | number | null | undefined,
  locale: Locale = DEFAULT_LOCALE,
): string {
  const d = toDate(value);
  if (!d) return '';
  const day = pad(d.getDate());
  const month = pad(d.getMonth() + 1);
  const year = d.getFullYear();
  return DATE_ORDER[locale] === 'mdy' ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
}

/** Data ISO (yyyy-mm-dd) para gravar. Sempre independente de idioma. */
export function toISODate(value: Date | string | number | null | undefined): string {
  const d = toDate(value);
  if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Constrói a data só se os componentes forem realmente válidos (pega 31/02). */
function build(year: number, month: number, day: number): Date | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return d;
}

/**
 * Lê uma data digitada ou colada. Devolve `null` quando não dá para ler com
 * segurança — o caller decide se avisa o usuário ou ignora a célula.
 */
export function parseUserDate(input: string, locale: Locale = DEFAULT_LOCALE): Date | null {
  const trimmed = String(input ?? '').trim();
  if (!trimmed) return null;

  // 1. ISO: yyyy-mm-dd ou yyyy/mm/dd. Sem ambiguidade.
  const iso = trimmed.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (iso) return build(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  // 2. Dois componentes e o ano, separados por / - ou .
  const parts = trimmed.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (!parts) return null;

  const a = Number(parts[1]);
  const b = Number(parts[2]);
  let year = Number(parts[3]);
  if (year < 100) year += 2000;

  const localeFirst = DATE_ORDER[locale] === 'mdy'
    ? build(year, a, b)   // mês primeiro
    : build(year, b, a);  // dia primeiro
  if (localeFirst) return localeFirst;

  // 3. A ordem do idioma é impossível: só resta uma leitura válida.
  return DATE_ORDER[locale] === 'mdy' ? build(year, b, a) : build(year, a, b);
}
